import { CLOUD_NAME, cloudinaryUrl, carTags, getCategoryColor } from '../config/cloudinary';

// Shape returned by Cloudinary's Resource List API
interface CloudinaryResource {
  public_id: string;
  version: number;
  format: string;
  width: number;
  height: number;
  type: string;
  created_at: string;
}

interface CloudinaryListResponse {
  resources: CloudinaryResource[];
  updated_at: string;
}

// Shape used by the Projects page
export interface ProjectImage {
  id: string;
  url: string;
  thumbUrl: string;
  alt: string;
}

export interface ProjectGroup {
  id: string;
  category: string;
  categoryColor: string;
  images: ProjectImage[];
}

// Fetch images for a single tag from Cloudinary's Resource List endpoint
async function fetchImagesByTag(tag: string): Promise<CloudinaryResource[]> {
  const url = `https://res.cloudinary.com/${CLOUD_NAME}/image/list/${tag}.json`;
  const response = await fetch(url);

  if (!response.ok) {
    // Tag might not exist yet or have no images — return empty
    return [];
  }

  const data: CloudinaryListResponse = await response.json();
  return data.resources || [];
}

// Convert Cloudinary resources into ProjectImage format
function toProjectImages(resources: CloudinaryResource[], tag: string): ProjectImage[] {
  return resources.map((r, i) => ({
    id: `${tag}-${i}`,
    url: cloudinaryUrl(r.public_id),
    thumbUrl: cloudinaryUrl(r.public_id, { width: 600, height: 450 }),
    alt: `${tag} photo ${i + 1}`,
  }));
}

// Fetch all project groups from Cloudinary based on the configured car tags.
// Returns only groups that have at least one image.
export async function fetchAllProjects(): Promise<ProjectGroup[]> {
  const results = await Promise.allSettled(
    carTags.map(async (car, index) => {
      const resources = await fetchImagesByTag(car.tag);
      if (resources.length === 0) return null;

      return {
        id: car.tag,
        category: car.title,
        categoryColor: getCategoryColor(index),
        images: toProjectImages(resources, car.tag),
      } as ProjectGroup;
    })
  );

  return results
    .map((r) => (r.status === 'fulfilled' ? r.value : null))
    .filter((g): g is ProjectGroup => g !== null);
}
